import { Injectable, signal, computed } from '@angular/core';
import { SharepointConfigurationEntity } from '../../domain/entities/sharepoint-configuration.entity';

/**
 * SharePoint Configuration state management using Angular signals.
 * Manages SharePoint configuration state (singleton per tenant).
 */
@Injectable({ providedIn: 'root' })
export class SharepointConfigurationsState {
  private readonly _sharepointConfiguration = signal<SharepointConfigurationEntity | null>(null);
  private readonly _loadingSharepointConfiguration = signal(false);
  private readonly _savingSharepointConfiguration = signal(false);
  private readonly _errorSharepointConfiguration = signal<string | null>(null);

  // Read-only signals
  public readonly sharepointConfiguration = this._sharepointConfiguration.asReadonly();
  public readonly loadingSharepointConfiguration = this._loadingSharepointConfiguration.asReadonly();
  public readonly savingSharepointConfiguration = this._savingSharepointConfiguration.asReadonly();
  public readonly errorSharepointConfiguration = this._errorSharepointConfiguration.asReadonly();

  // Computed signals
  public readonly hasSharepointConfiguration = computed(() => this._sharepointConfiguration() !== null);
  public readonly isConfigured = computed(() => {
    const config = this._sharepointConfiguration();
    return config !== null && !!config.clientId && !!config.sharepointTenantId;
  });

  /**
   * Sets the SharePoint configuration.
   * @param configuration - SharePoint configuration entity or null
   */
  public setSharepointConfiguration(configuration: SharepointConfigurationEntity | null): void {
    this._sharepointConfiguration.set(configuration);
  }

  /**
   * Sets the loading state for SharePoint configuration.
   * @param loading - Loading state
   */
  public setLoadingSharepointConfiguration(loading: boolean): void {
    this._loadingSharepointConfiguration.set(loading);
  }

  /**
   * Sets the saving state for SharePoint configuration.
   * @param saving - Saving state
   */
  public setSavingSharepointConfiguration(saving: boolean): void {
    this._savingSharepointConfiguration.set(saving);
  }

  /**
   * Sets an error message for SharePoint configuration.
   * @param error - Error message
   */
  public setErrorSharepointConfiguration(error: string | null): void {
    this._errorSharepointConfiguration.set(error);
  }

  /**
   * Clears all SharePoint configuration state.
   */
  public clear(): void {
    this._sharepointConfiguration.set(null);
    this._loadingSharepointConfiguration.set(false);
    this._savingSharepointConfiguration.set(false);
    this._errorSharepointConfiguration.set(null);
  }
}
